import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CompanyObjectivePeriodStatus } from 'src/app/enums/company-objective-period-status.enum';
import { ActiveCompanyObjective } from 'src/app/models/active-company-objective.model';
import {
  CompanyObjectiveKeyResultComponent,
  metricStateFromThresholds,
  scoreStateFromScoring,
} from './company-objective-key-result.component';

@Component({
  selector: 'app-company-product-objective',
  templateUrl: './company-product-objective.component.html',
  styleUrls: ['./company-product-objective.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DecimalPipe, MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule, CompanyObjectiveKeyResultComponent],
})
export class CompanyProductObjectiveComponent {
  private readonly router = inject(Router);
  readonly objective = input.required<ActiveCompanyObjective>();
  readonly show_company_product_context = input(true);
  readonly layout = input<'dashboard' | 'management'>('dashboard');
  readonly period_status = input<CompanyObjectivePeriodStatus>(CompanyObjectivePeriodStatus.Active);
  readonly timeline_progress = input<number | null>(null);
  readonly show_objective_summary = input(true);
  readonly level_label = input('Objetivo activo');
  readonly selected = input(false);
  readonly clickable = input(false);
  readonly can_update = input(false);
  readonly can_delete = input(false);
  readonly warnings = input<string[]>([]);

  readonly cardClick = output<void>();
  readonly view = output<void>();
  readonly edit = output<void>();
  readonly delete = output<void>();

  readonly vm = computed(() => {
    const objective = this.objective();
    const now = new Date();
    const end_date = new Date(objective.end_date);
    const days_left = Math.max(0, Math.ceil((end_date.getTime() - now.getTime()) / 86400000));
    const key_results = [...objective.company_objective_key_results].sort((left, right) => left.order - right.order);

    const scoring_values = key_results.map(key_result => key_result.scoring).filter((score): score is number => score !== null);

    const total_scoring = scoring_values.length > 0 ? scoring_values.reduce((sum, score) => sum + score, 0) / scoring_values.length : null;
    const has_low_metric = key_results.some(
      key_result =>
        metricStateFromThresholds(key_result.current_value, key_result.acceptable_value, key_result.good_value, key_result.evaluation) ===
        'low'
    );
    const has_mid_metric = key_results.some(
      key_result =>
        metricStateFromThresholds(key_result.current_value, key_result.acceptable_value, key_result.good_value, key_result.evaluation) ===
        'mid'
    );
    const has_good_metric = key_results.some(
      key_result =>
        metricStateFromThresholds(key_result.current_value, key_result.acceptable_value, key_result.good_value, key_result.evaluation) ===
        'good'
    );
    const objective_health_state = has_low_metric ? 'low' : has_mid_metric || !has_good_metric ? 'mid' : 'good';
    const timeline_progress = this.timeline_progress();
    const score_behind_time =
      this.period_status() === CompanyObjectivePeriodStatus.Active &&
      total_scoring !== null &&
      timeline_progress !== null &&
      total_scoring / 10 < timeline_progress;
    const has_key_results = key_results.length > 0;
    const has_score = total_scoring !== null;
    const show_score_chip = has_score;
    const show_score_bar = this.layout() === 'management' && this.period_status() !== CompanyObjectivePeriodStatus.Future && has_score;
    const show_timeline_bar = this.layout() === 'management' && this.period_status() === CompanyObjectivePeriodStatus.Active;
    const show_empty_score =
      this.layout() === 'management' && has_key_results && !has_score && this.period_status() !== CompanyObjectivePeriodStatus.Past;

    return {
      objective,
      key_results,
      days_left,
      totalScoring: total_scoring,
      objectiveHealthState: objective_health_state,
      score_behind_time,
      show_score_chip,
      show_score_bar,
      show_timeline_bar,
      show_empty_score,
      score_pct: total_scoring === null ? 0 : (total_scoring / 10) * 100,
      timeline_pct: timeline_progress === null ? 0 : timeline_progress * 100,
    };
  });

  openExternalHealthMetrics(): void {
    const company_product_id = this.objective().company_product_id;
    if (!company_product_id) {
      return;
    }

    this.router.navigate(['/employees', 'development', 'company-products', company_product_id, 'okr-and-metrics'], {
      fragment: 'health-metrics',
    });
  }

  onCardClick(): void {
    if (!this.clickable()) {
      return;
    }

    this.cardClick.emit();
  }
}
